// AuthProvider.jsx
import React from "react";
import { AuthContext } from "./AuthContext";
import { EventType } from "@azure/msal-browser"; 
import { initializeUser } from "../fetchers/user";

const STORAGE_KEY = "auth:user";

export default function AuthProvider({ msalInstance, children }) {
    const [state, setState] = React.useState(() => {
        const cached = sessionStorage.getItem(STORAGE_KEY);
        if (cached) {
            try {
                const parsed = JSON.parse(cached);
                return { user: parsed.user, roleIds: parsed.roleIds || [], isAuthLoading: false };
            } catch { }
        }
        return { user: null, roleIds: [], isAuthLoading: true };
    });

    const loadUserFromBackend = React.useCallback(async () => {
        try {
            const me = await initializeUser();
            const roleIds = (me.roles || []).map(r => (typeof r === "number" ? r : r.id));
            const next = { user: me, roleIds, isAuthLoading: false };
            setState(next);
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ user: me, roleIds }));
        } catch (e) {
            setState(s => ({ ...s, isAuthLoading: false }));
        }
    }, []);

    React.useEffect(() => {
        const accounts = msalInstance.getAllAccounts();
        if (!state.user && accounts.length > 0) {
            loadUserFromBackend();
        } else {
            setState(s => ({ ...s, isAuthLoading: false }));
        }
    }, [msalInstance, state.user, loadUserFromBackend]);

    React.useEffect(() => {
        const cbId = msalInstance.addEventCallback((event) => {
            if (
                event.eventType === EventType.LOGIN_SUCCESS ||
                event.eventType === EventType.SSO_SILENT_SUCCESS ||
                event.eventType === EventType.ACCOUNT_ADDED
            ) {
                loadUserFromBackend();
            }
            if (event.eventType === EventType.LOGOUT_SUCCESS) {
                sessionStorage.removeItem(STORAGE_KEY);
                setState({ user: null, roleIds: [], isAuthLoading: false });
            }
        });
        return () => {
            if (cbId) msalInstance.removeEventCallback(cbId);
        };
    }, [msalInstance, loadUserFromBackend]);

    React.useEffect(() => {
        const onRolesMaybeChanged = () => {
            loadUserFromBackend();
        };

        window.addEventListener("auth:roles-maybe-changed", onRolesMaybeChanged);
        return () => {
            window.removeEventListener("auth:roles-maybe-changed", onRolesMaybeChanged);
        };
    }, [loadUserFromBackend]);

    return (
        <AuthContext.Provider value={state}>
            {children}
        </AuthContext.Provider>
    );
}
